'use client'

/**
 * Purpose: Post-scan confirmation telling the user what actually happened to their
 *   photo — deleted, scheduled for deletion, or stored for research until a date —
 *   before the survey continues.
 * Inputs: locale, the stored ISO `deletionDate` from the analysis response, whether
 *   the image is already deleted, the recorded `researchConsent`, and onContinue.
 * Outputs: One of three copy variants plus a continue control; renders nothing when
 *   there is no confirmable outcome to report.
 */

import { useTranslations } from 'next-intl'
import HoverGradientButton from '@/components/buttons/HoverGradientButton/HoverGradientButton'
import Link from 'next/link'
import { formatCalendarDate } from '@lib/format-date'

export type SurveyCameraConfirmationProps = {
  locale: string
  /** ISO date exactly as stored on the retention record — never recomputed here. */
  deletionDate: string | null
  /** True only when deletion already completed and was verified server-side. */
  imageDeleted: boolean
  researchConsent: boolean
  onContinue: () => void
}

/**
 * Which of the spec's three outcomes to state. Deletion wins over any date:
 * an already-deleted image has nothing left to schedule. A verified deletion is
 * the only thing that earns the "deleted" copy, so a failed delete falls through
 * to the stored schedule instead of claiming the photo is gone.
 */
function variantFor(
  imageDeleted: boolean,
  researchConsent: boolean,
): 'deleted' | 'scheduled' | 'research' {
  if (imageDeleted) return 'deleted'
  return researchConsent ? 'research' : 'scheduled'
}

export function SurveyCameraConfirmation({
  locale,
  deletionDate,
  imageDeleted,
  researchConsent,
  onContinue,
}: SurveyCameraConfirmationProps) {
  const t = useTranslations('survey.photo.retention')
  const variant = variantFor(imageDeleted, researchConsent)

  // An absolute calendar date, formatted in UTC so the day shown matches the day
  // the retention policy computed and never shifts with the viewer's offset.
  const formattedDate = deletionDate
    ? formatCalendarDate(deletionDate, locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : ''

  return (
    <div id="survey-camera-capture-confirmation" className="survey-camera-capture-outcome">
      <h2 id="survey-camera-confirmation-title">{t(`${variant}.title`)}</h2>
      {/* Research variant also names where to change the decision, so the link
          renders through t.rich rather than as plain text. */}
      {variant === 'research' ? (
        <p
          id="survey-camera-confirmation-research-description"
          className="survey-camera-capture-outcome-description"
        >
          {t.rich('research.description', {
            date: formattedDate,
            settingsLink: (chunks) => (
              <Link
                id="survey-camera-confirmation-settings-link"
                href={`/${locale}/account`}
                className="survey-camera-capture-link"
              >
                {chunks}
              </Link>
            ),
          })}
        </p>
      ) : (
        <p
          id="survey-camera-confirmation-description"
          className="survey-camera-capture-outcome-description"
        >
          {/* The deleted variant states a past fact and takes no date; only the
              scheduled one names a future calendar date. */}
          {variant === 'deleted'
            ? t('deleted.description')
            : t('scheduled.description', { date: formattedDate })}
        </p>
      )}
      <div
        id="survey-camera-confirmation-actions"
        className="survey-camera-capture-outcome-actions"
      >
        <HoverGradientButton
          id="survey-camera-confirmation-continue"
          onClick={onContinue}
          className="cursor-pointer"
        >
          {t('continue')}
        </HoverGradientButton>
      </div>
    </div>
  )
}
